import React from "react";

export interface iBasket {
    items: Item<any>[];
}

export interface ChartPieComponentProps {
    data: iBasket;
}

export interface ChartPieProps {
    data: any[];
    styleParam?: React.CSSProperties;
    buildLabels?: (data: any[]) => string[];
    generateColors?: (count: number) => string[];
}

export interface ChartBarProductsProps {
    data: Item<DataRow>[];
}

export interface ChartBarTerpenesProps {
    data: Item<TerpeneDataRow>[];
}

export interface ChartData {
    labels: string[];
    datasets: {
        data: number[];
        backgroundColor: string[];
    }[];
}

export enum ViewTypes {
    DIRECT = 'direct',
    PERCENTAGE = 'percentage',
    NORMALIZED = 'normalized',
    LOGARITHMIC = 'logarithmic'
}

export type UnitType = 'mg' | 'µg' | 'microg' | 'ng';
export type DenominatorType = 'mg' | 'item';

export interface DataRow {
    PRODUCT?: string;
    ANALYSIS_TYPE?: string;
    COMPOUND_NAME: string;
    INCHIKEY?: string;
    AVERAGE?: number | string | null;
    CONCENTRATION?: number | string | null;
    STD_DEV?: number | string | null;
    LOD?: number | string | null;
    LOQ?: number | string | null;
    NUMBER_OF_REPLICATES?: number | string | null;
    NUMBER_OF_ITEMS_PER_REPLICATE?: number | string | null;
    TEST_METHOD_CODE?: string | null;
    REPORT_PROJECT_ID?: string | null;
    UNIT?: string;
    CLASS?: string;
}

export interface PKDataRow {
    PRODUCT?: string;
    DOSE_MG_KG?: number | string | null;
    BODYWEIGHT_KG?: number | string | null;
    DOSE_MG?: number | string | null;
    TMAX_H?: number | string | null;
    CMAX_NG_ML?: number | string | null;
    HALF_LIFE_H?: number | string | null;
    AUC_NG_H_ML?: number | string | null;
    CMAX_DOSEN?: number | string | null;
    AUC_DOSEN?: number | string | null;
    CMAX_DOSE?: number | string | null;
    AUC_DOSE?: number | string | null;
    CMAX_TMAX?: number | string | null;
}

export type PKFieldKey = Exclude<keyof PKDataRow, 'PRODUCT'>;

export interface TerpeneDataRow {
    NAME: string;
    CONCENTRATION?: number | string | null;
    PRODUCT?: string;
    INCHIKEY?: string;
    UNIT?: string;
}

export interface Item<T> {
    HEADER?: string;
    TITLE?: string;
    DATA: T[];
    _id?: string;
}

export interface ChartPieProductExtendedProps extends ChartPieComponentProps {
    selectedProducts: string[];
    selectedClass: string;
    compoundFilter: 'all' | 'inCommon' | 'notInCommon';
    showNonNumeric: boolean;
    selectedUnit: UnitType;
    selectedDenominator: DenominatorType;
    diseaseInchiKeys: string[];
    selectedCompounds: string[];
}

export interface ChartBarProductsExtendedProps extends ChartBarProductsProps {
    selectedProducts: string[];
    setSelectedProducts: (products: string[]) => void;
    compoundFilter: 'all' | 'inCommon' | 'notInCommon';
    setCompoundFilter: (value: 'all' | 'inCommon' | 'notInCommon') => void;
    showNonNumeric: boolean;
    setShowNonNumeric: (value: boolean) => void;
    selectedClass: string;
    setSelectedClass: (value: string) => void;
    selectedUnit: UnitType;
    setSelectedUnit: (value: UnitType) => void;
    selectedDenominator: DenominatorType;
    setSelectedDenominator: (value: DenominatorType) => void;
    selectedDiseaseCategory: string;
    setSelectedDiseaseCategory: (value: string) => void;
    diseaseInchiKeys: string[];
    setDiseaseInchiKeys: (inchiKeys: string[]) => void;
    selectedCompounds: string[];
    setSelectedCompounds: (compounds: string[]) => void;
    selectedAnalysisType: string;
    setSelectedAnalysisType: (value: string) => void;
}

export interface ChartBarComparisonExtendedProps extends ChartBarProductsProps {
    selectedProducts: string[];
    setSelectedProducts: (products: string[]) => void;
    compoundFilter: 'all' | 'inCommon' | 'notInCommon';
    setCompoundFilter: (value: 'all' | 'inCommon' | 'notInCommon') => void;
    showNonNumeric: boolean;
    setShowNonNumeric: (value: boolean) => void;
    selectedClass: string;
    setSelectedClass: (value: string) => void;
    selectedUnit: UnitType;
    setSelectedUnit: (value: UnitType) => void;
    selectedDenominator: DenominatorType;
    setSelectedDenominator: (value: DenominatorType) => void;
    selectedDiseaseCategory: string;
    setSelectedDiseaseCategory: (value: string) => void;
    diseaseInchiKeys: string[];
    setDiseaseInchiKeys: (inchiKeys: string[]) => void;
    selectedCompounds: string[];
    setSelectedCompounds: (compounds: string[]) => void;
}

export interface ChartTableProductsProps extends ChartBarProductsProps {
    selectedProducts: string[];
    setSelectedProducts: (products: string[]) => void;
    compoundFilter: 'all' | 'inCommon' | 'notInCommon';
    setCompoundFilter: (value: 'all' | 'inCommon' | 'notInCommon') => void;
    showNonNumeric: boolean;
    setShowNonNumeric: (value: boolean) => void;
    selectedClass: string;
    setSelectedClass: (value: string) => void;
    selectedUnit: UnitType;
    setSelectedUnit: (value: UnitType) => void;
    selectedDenominator: DenominatorType;
    setSelectedDenominator: (value: DenominatorType) => void;
    selectedDiseaseCategory: string;
    setSelectedDiseaseCategory: (value: string) => void;
    diseaseInchiKeys: string[];
    setDiseaseInchiKeys: (inchiKeys: string[]) => void;
    selectedCompounds: string[];
    setSelectedCompounds: (compounds: string[]) => void;
}
